import React, { useState } from 'react';
import './ParentOnboarding.css';

const ParentOnboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-container">
        {/* Progress Indicator */}
        <div className="onboarding-progress">
          <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
        </div>

        {/* Step 1: Boas-vindas */}
        {currentStep === 1 && (
          <div className="onboarding-step">
            <div className="step-icon">🌟</div>
            <h1>Bem-vindo(a) ao Missão de Heróis!</h1>
            <p className="step-subtitle">
              Você acaba de entrar em um espaço criado para ajudar sua criança a desenvolver 
              <strong> responsabilidade</strong>, <strong>autonomia</strong>, <strong>constância</strong> e 
              <strong> boas escolhas</strong> — de forma leve, divertida e conectada à rotina real da família.
            </p>
            <div className="welcome-highlights">
              <div className="highlight-card">
                <span className="highlight-icon">🎯</span>
                <h3>Estruture a rotina</h3>
                <p>Organize as tarefas diárias de forma visual e motivadora</p>
              </div>
              <div className="highlight-card">
                <span className="highlight-icon">✨</span>
                <h3>Desenvolva autonomia</h3>
                <p>Incentive sua criança a assumir responsabilidades</p>
              </div>
              <div className="highlight-card">
                <span className="highlight-icon">🚀</span>
                <h3>Celebre o progresso</h3>
                <p>Reconheça o esforço e constância, não só o resultado</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Como aproveitar ao máximo */}
        {currentStep === 2 && (
          <div className="onboarding-step">
            <div className="step-icon">💡</div>
            <h1>Como usar da melhor forma</h1>
            <p className="step-subtitle">
              O Missão de Heróis não é um sistema de troca simples. É uma ferramenta educativa.
            </p>

            <div className="content-sections">
              <div className="content-section">
                <h3>🎁 Use recompensas de forma saudável</h3>
                <ul>
                  <li><strong>Proporcionais ao esforço:</strong> Tarefas maiores valem mais HeroCoins</li>
                  <li><strong>Inclua experiências:</strong> Tempo juntos, passeios, escolher o filme da noite</li>
                  <li><strong>Diminuem com o tempo:</strong> O progresso vira a motivação principal</li>
                </ul>
              </div>

              <div className="content-section">
                <h3>📝 Nem tudo precisa virar missão</h3>
                <ul>
                  <li><strong>Comece pequeno:</strong> Prefira tarefas simples e metas alcançáveis</li>
                  <li><strong>Algumas ficam fora:</strong> Nem toda obrigação precisa estar no app</li>
                  <li><strong>O app é um apoio,</strong> não um contrato</li>
                </ul>
              </div>

              <div className="content-section">
                <h3>💰 HeroCoins ensinam escolhas</h3>
                <ul>
                  <li><strong>Não são prêmio imediato:</strong> A criança acumula e decide quando usar</li>
                  <li><strong>Educação financeira:</strong> Ensina planejamento e autocontrole</li>
                  <li><strong>Aprender com escolhas:</strong> Gastar tudo agora ou guardar para algo maior?</li>
                </ul>
              </div>

              <div className="content-section special-note">
                <h3>⚡ Para crianças com TDAH</h3>
                <p>
                  Missões curtas + feedback rápido + recompensas frequentes no início = 
                  ajuda a manter foco, reduzir ansiedade e desenvolver rotina.
                </p>
              </div>
            </div>

            <div className="important-reminder">
              <strong>Lembre-se:</strong> O papel mais importante ainda é o seu. 
              O app ajuda a organizar, mas nada substitui conversa, afeto e presença.
            </div>
          </div>
        )}

        {/* Step 3: Por onde começar */}
        {currentStep === 3 && (
          <div className="onboarding-step">
            <div className="step-icon">🚀</div>
            <h1>Por onde começar?</h1>
            <p className="step-subtitle">
              Siga esta ordem para configurar o sistema e começar sua jornada:
            </p>

            <div className="getting-started-steps">
              <div className="start-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>👶 Cadastre suas crianças</h3>
                  <p>
                    Vá em <strong>"Gerenciar Crianças"</strong> e adicione cada criança com nome, 
                    data de nascimento e série. Cada uma terá seu próprio perfil.
                  </p>
                </div>
              </div>

              <div className="start-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>🎁 Configure as recompensas</h3>
                  <p>
                    Em <strong>"Recompensas"</strong>, crie opções que sua criança pode trocar por HeroCoins. 
                    Lembre-se: experiências e tempo juntos são ótimas recompensas!
                  </p>
                </div>
              </div>

              <div className="start-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>📋 Crie as primeiras tarefas</h3>
                  <p>
                    Comece com <strong>2 ou 3 tarefas simples</strong> que a criança já faz ou pode fazer facilmente. 
                    Pontuação baixa no início!
                  </p>
                </div>
              </div>

              <div className="start-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>🔑 Gere o código de acesso</h3>
                  <p>
                    No perfil da criança, gere o <strong>"Código de Pareamento"</strong>. 
                    Sua criança usa esse código para fazer login no app dela e começar a missão!
                  </p>
                </div>
              </div>
            </div>

            <div className="final-message">
              <h3>🌟 Você está pronto(a)!</h3>
              <p>
                O maior prêmio não é a recompensa — é a evolução. 
                Combine expectativas com sua criança e comece pequeno.
              </p>
              <p className="final-tip">
                <strong>Dica:</strong> Menos conflitos no dia a dia + mais iniciativa = você está no caminho certo! ✨
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="onboarding-actions">
          {currentStep < 3 && (
            <button className="btn-skip" onClick={handleSkip}>
              Pular tutorial
            </button>
          )}
          <button className="btn-next" onClick={handleNext}>
            {currentStep < 3 ? 'Próximo' : 'Começar jornada! 🚀'}
          </button>
        </div>

        <div className="step-indicator">
          Passo {currentStep} de 3
        </div>
      </div>
    </div>
  );
};

export default ParentOnboarding;
